import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import MobileSheetDropdown from '../../../components/ui/MobileSheetDropdown'
import { getManagerLists } from '../api/managerListApi'
import type { ManagerList } from '../types'

interface Props {
  /** Seçilen liste — çağıran, listenin kişilerini kendi kümesine ekler. */
  onPick: (list: ManagerList) => void
  placeholder: string
  disabled?: boolean
  /**
   * Odaklanan girdinin `id`'si — seçici görünür bir `<label htmlFor>` altında duruyorsa (Ajanda'nın
   * "Grup Daveti" alanı) erişilebilir ad o etiketten gelir. Verilmezse ad `placeholder`'dır.
   */
  inputId?: string
}

/**
 * **İsim listesi seçici** (115) — kişi aramasının yanında durur ve bir listeyi tek seçimle ekler.
 *
 * <p>Listeler Admin → Parametreler → Liste Yönetimi'nde hazırlanır. Seçim yapıldığında bu bileşen
 * **kişi eklemez**; yalnız seçilen listeyi bildirir. Genişletmeyi (kişileri kümeye katma, sicil
 * bazında tekilleştirme, etiketleme) çağıran yapar — kurallar her yüzeyde aynı değil: prensip
 * kararında sorumlular liste adı altında **gruplanarak**, tutanakta katılımcılar **tek tek** gösterilir.
 *
 * <p><b>Uygulamanın kendi aranabilir dropdown kalıbı kullanılır</b> (`MobileSheetDropdown` + `filter`)
 * — Toplantı Yönetimi filtrelerindeki seçicilerle aynı bileşen, aynı görünüm. Önce `AutoComplete`'in
 * `dropdown` kipi denendi ve bırakıldı: PrimeReact onu **ayrı, vurgu renginde kare bir düğme** olarak
 * çiziyor ve uygulamada eşi olmayan bir form öğesi çıkıyordu (kullanıcı bildirimi 01.10.2026). Burada
 * seçilen şey **sonlu bir kümeden** bir öğedir; serbest metin araması değil, dropdown doğru kalıptır.
 *
 * <p><b>Seçim kalıcı DEĞİL</b>: bu bir alan değil, bir **eylem** tetikleyicisidir — liste seçilir,
 * kişiler eklenir, kutu boş kalır (`value={null}`). Seçili kalsaydı kullanıcı aynı listeyi ikinci kez
 * ekleyemez ve kutu, karşılığı olmayan bir "seçili liste" durumu gösterirdi.
 *
 * <p><b>Filtreleme istemcide</b> (`filter`): kardeş seçicilerin tamamı böyle çalışıyor ve parametre
 * ölçeği küçük. Uç tek sayfa döndürdüğü için liste sayısı sayfa boyutunu (200) aşarsa fazlası
 * seçicide **görünmez** — o gün kişi seçicisindeki gibi sunucu taraflı aramaya geçilmelidir.
 *
 * <p><b>Yetki notu:</b> listeleri okuyan uç `MANAGER_LIST_MANAGE` ister. Bu bileşeni kullanan iki
 * ekranın yetkileri (`MINUTES_CREATE`, `PRINCIPLE_DECISION_MANAGE`) 01.10.2026'da ölçüldü ve **aynı
 * rol kümesine** (1 · 3 · 4) bağlı, yani seçiciyi görebilen herkes listeleri okuyabiliyor. Kümeler
 * ileride ayrışırsa seçici sessizce boş kalır (sorgu 403 alır) — o gün listelere ayrı bir görüntüleme
 * yetkisi gerekir. **Ajanda (125)** bu kümeden geniştir (`AGENDA_VIEW` tüm rollerde); orada seçici
 * yalnız `MANAGER_LIST_MANAGE` sahibine **çizilir** (bkz. `AgendaFormDialog`).
 */
export default function ManagerListPicker({
  onPick,
  placeholder,
  disabled,
  inputId,
}: Readonly<Props>) {
  const { t } = useTranslation()

  const lists = useQuery({
    queryKey: ['manager-lists', 'picker'],
    queryFn: () => getManagerLists(),
    staleTime: 60_000,
  })

  const all = lists.data?.content ?? []
  const options = all.map((l) => ({ label: l.name, value: l.id, personCount: l.personCount }))

  return (
    <MobileSheetDropdown
      className="ml-picker"
      value={null}
      options={options}
      onChange={(e) => {
        const picked = all.find((l) => l.id === e.value)
        if (picked) onPick(picked)
      }}
      placeholder={placeholder}
      inputId={inputId}
      // `aria-label` görünür etiketi ezerdi: etiket bağlıysa ad ondan gelsin.
      aria-label={inputId ? undefined : placeholder}
      // Liste yoksa seçici kapalı: açılıp boş panel göstermek kullanıcıya hiçbir şey söylemezdi.
      disabled={disabled || all.length === 0}
      filter
      // Panel kapanınca filtre metni sıfırlanır: bu kutu bir ALAN değil, eylem tetikleyicisi —
      // kutu placeholder gösterirken panelin eski aramayla süzülü açılması kafa karıştırırdı
      // (PrimeReact varsayılanı false; push kapısı bulgusu 01.10.2026).
      resetFilterOnHide
      filterPlaceholder={placeholder}
      emptyMessage={t('managerLists.picker.empty')}
      emptyFilterMessage={t('managerLists.picker.empty')}
      itemTemplate={(option: { label: string; personCount: number }) => (
        <div className="ml-picker-item">
          <span>{option.label}</span>
          <span className="ml-picker-count">
            {t('managerLists.picker.personCount', { count: option.personCount })}
          </span>
        </div>
      )}
    />
  )
}
